import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { leadAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminLeads() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  
  const loadLeads = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const result = await leadAPI.list(params);
      setLeads(result.leads || []);
    } catch (error) {
      console.error('Load leads error:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);
  
  useEffect(() => {
    loadLeads();
  }, [loadLeads]);
  
  const updateStatus = async (id, status) => {
    try {
      await leadAPI.update(id, { status });
      toast.success('Status updated');
      loadLeads();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  };
  
  return (
    <div className="min-h-screen bg-muted/30">
      <div className="border-b bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Leads</h1>
            <Link to="/admin">
              <Button variant="outline">Back</Button>
            </Link>
          </div>
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="admin-leads-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--ap-blue)' }} />
          </div>
        ) : leads.length === 0 ? (
          <Card className="rounded-xl">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No leads found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {leads.map(lead => (
              <Card key={lead.id} className="rounded-xl" data-testid="admin-leads-row">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{lead.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{lead.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="secondary">{lead.source}</Badge>
                      <Select value={lead.status} onValueChange={(value) => updateStatus(lead.id, value)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Company</p>
                      <p className="font-medium">{lead.company}</p>
                    </div>
                    {lead.phone && (
                      <div>
                        <p className="text-muted-foreground mb-1">Phone</p>
                        <p className="font-medium">{lead.phone}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground mb-1">Created</p>
                      <p className="font-medium">{new Date(lead.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {lead.message && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-1">Message</p>
                      <p className="text-sm">{lead.message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}